import { FormField } from '@crocodille/common';
import { isSome } from '@crocodille/util';
import { useCallback, useEffect, useRef, useState } from 'react';
import Input, { InputProps } from '../input/input';
import cn from 'classnames';
import styles from './file-input.module.scss';

export interface FileInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  readonly onChange?: (value: File[] | null) => void;
  readonly clearFile?: boolean;
}

type FieldProps = 'onChange' | 'disabled' | 'onBlur' | 'required';

export function fieldToFileInputProps(
  field: Pick<FormField<File[] | null>, 'error' | FieldProps>,
) {
  return {
    invalid: isSome(field.error),
    onChange: field.onChange,
    onBlur: field.onBlur,
    disabled: field.disabled,
    required: field.required,
  } satisfies Pick<FileInputProps, 'invalid' | FieldProps>;
}

const FileInput: React.FC<FileInputProps> = ({
  onChange,
  className,
  clearFile,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [key, setKey] = useState(0);

  const handleChange = useCallback(() => {
    const files = inputRef.current?.files ?? null;
    const fileList = files ? Array.from(files) : null;

    onChange?.(fileList);
  }, [onChange]);

  useEffect(() => {
    if (clearFile && inputRef.current) {
      setKey((prev) => prev + 1);
      inputRef.current.value = '';
    }
  }, [clearFile]);

  return (
    <Input
      key={key}
      className={cn(styles.container, className, styles.fileInput)}
      {...props}
      ref={inputRef}
      type="file"
      onChange={handleChange}
    />
  );
};

export default FileInput;
